import React, { useMemo, useRef, useState, useEffect } from 'react'
import './AudioBarChart.css'
import { TranscriptData } from '@dataTypes/message.types'

interface Segment {
  start: number
  end: number
  style?: 'solid' | 'striped'
}

interface AudioBarChartProps {
  transcriptData: TranscriptData[]
  duration: number
  getSegmentStyle?: (data: TranscriptData, index: number) => 'solid' | 'striped'
}

function AudioBarChart({
  transcriptData,
  duration,
  getSegmentStyle,
}: AudioBarChartProps): React.ReactElement {
  const trackContentRef = useRef<HTMLDivElement | null>(null)
  const [actualWidth, setActualWidth] = useState<number>(400)
  const timeToSeconds = (time: string): number => {
    const parts = time.split(':')
    const arrayLength = parts.length
    const seconds = parseInt(parts[arrayLength - 1])
    const minutes = parseInt(parts[arrayLength - 2])
    let hour = 0
    if (arrayLength === 3) hour = parseInt(parts[0])
    return hour * 60 * 60 + minutes * 60 + seconds
  }

  const speakerSegments = useMemo(() => {
    const segments: Record<string, Segment[]> = {
      Caller: [],
      Clinic: [],
    }

    if (!transcriptData || transcriptData.length === 0 || duration <= 0) {
      return segments
    }

    transcriptData.forEach((data, index) => {
      const speaker = data.speaker === 'Client' ? 'Caller' : data.speaker
      const startTime = timeToSeconds(data.time)
      const endTime =
        index + 1 < transcriptData.length
          ? timeToSeconds(transcriptData[index + 1].time)
          : duration

      // Ensure times are within valid range
      const clampedStartTime = Math.max(0, Math.min(startTime, duration))
      const clampedEndTime = Math.max(
        clampedStartTime,
        Math.min(endTime, duration),
      )

      const start = (clampedStartTime / duration) * actualWidth
      const end = (clampedEndTime / duration) * actualWidth

      if (!segments[speaker]) {
        segments[speaker] = []
      }

      // Only add segment if it has valid width
      if (end > start) {
        const style = getSegmentStyle ? getSegmentStyle(data, index) : 'solid'
        segments[speaker].push({
          start,
          end,
          style,
        })
      }
    })

    return segments
  }, [transcriptData, duration, actualWidth, getSegmentStyle])

  // Measure container width and update actualWidth
  useEffect(() => {
    const updateWidth = (): void => {
      if (trackContentRef.current) {
        const containerWidth = trackContentRef.current.clientWidth
        setActualWidth(Math.max(100, containerWidth))
      }
    }

    updateWidth()
    window.addEventListener('resize', updateWidth)

    // Use ResizeObserver for more accurate measurements
    let resizeObserver: ResizeObserver | null = null
    if (trackContentRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(updateWidth)
      resizeObserver.observe(trackContentRef.current)
    }

    return () => {
      window.removeEventListener('resize', updateWidth)
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [])

  const renderSegment = (
    segment: Segment,
    index: number,
    speaker: string,
  ): JSX.Element => {
    const segmentWidth = Math.max(segment.end - segment.start, 1) // Ensure minimum width
    const color = speaker === 'Clinic' ? 'var(--violet-400)' : 'var(--blue-400)'

    if (segment.style === 'striped') {
      return (
        <div
          key={index}
          className="bar-segment striped"
          style={{
            left: `${segment.start}px`,
            width: `${segmentWidth}px`,
            backgroundColor: color,
            color: color,
          }}
        />
      )
    }

    return (
      <div
        key={index}
        className="bar-segment"
        style={{
          left: `${segment.start}px`,
          width: `${segmentWidth}px`,
          backgroundColor: color,
        }}
      />
    )
  }

  return (
    <div className="audio-bar-chart">
      <div className="bar-track">
        <div className="track-label caller-label">Caller</div>
        <div className="track-content" ref={trackContentRef}>
          {speakerSegments.Caller?.map((segment, index) =>
            renderSegment(segment, index, 'Caller'),
          )}
        </div>
      </div>
      <div className="bar-track">
        <div className="track-label clinic-label">Clinic</div>
        <div className="track-content">
          {speakerSegments.Clinic?.map((segment, index) =>
            renderSegment(segment, index, 'Clinic'),
          )}
        </div>
      </div>
    </div>
  )
}

export default React.memo(AudioBarChart)
